import { getAuthoredCableEndpoints } from './AuthoredCableChannels';
import type {
  IChipInstanceModel,
  IConnectionModel,
  ITileManagerModel,
} from '@/interfaces/models';
import type { TileInstance } from '@/models/tiles/TileInstance';
import { TileType } from '@/models/TileTypes';
import { Point } from '@/models/Point';
import { Side } from '@/schemas/tiles';
import { getGlobalDirection, getLocalSide, getNeighborPosition } from './utils';
import { opposite } from '@/utils/direction';

/** Only adapter-only, full-mapping prototype paths have equivalent named-cable semantics. */
export function migrateAdapterCables(
  manager: ITileManagerModel,
  connections: IConnectionModel,
  instances: readonly IChipInstanceModel[],
) {
  const tiles = manager.tiles;
  if (!tiles.some((tile) => tile.meta.bundle)) return;
  const adapters = new Set(
    instances
      .filter((instance) => instance.chip?.kind === 'bundle-adapter')
      .map((instance) => instance.id as string),
  );
  const passiveEndpoints = getAuthoredCableEndpoints(
    instances.map(
      (instance) =>
        new Proxy(instance, {
          get: (target, key) =>
            key === 'bundleWidth' ? undefined : Reflect.get(target, key),
        }),
    ),
    tiles,
  );
  const passiveSides = new Map<string, Set<Side>>();
  const passiveOwners = new Map<string, typeof passiveEndpoints>();
  for (const endpoint of passiveEndpoints) {
    const sides = passiveSides.get(endpoint.tile.id) ?? new Set<Side>();
    sides.add(endpoint.side);
    passiveSides.set(endpoint.tile.id, sides);
    const owner = endpoint.tile.meta.partOfChip!;
    const list = passiveOwners.get(owner) ?? [];
    list.push(endpoint);
    passiveOwners.set(owner, list);
  }
  // Legacy Cable markers have been normalized to ordinary Wire on load. Only
  // accept ordinary-wire neighbors whose physical passive channel reaches an
  // adapter bundle port; arbitrary scalar wires do not prove a safe migration.
  const passiveByTile = new Map<string, typeof passiveEndpoints>();
  const passiveByChannel = new Map<string, typeof passiveEndpoints>();
  for (const endpoint of passiveEndpoints) {
    passiveByTile.set(endpoint.tile.id, [
      ...(passiveByTile.get(endpoint.tile.id) ?? []),
      endpoint,
    ]);
    passiveByChannel.set(endpoint.channelKey, [
      ...(passiveByChannel.get(endpoint.channelKey) ?? []),
      endpoint,
    ]);
  }
  type Transport = { tile: TileInstance; sides: Side[]; key: string };
  const transportQueue: Transport[] = [];
  const transportSeen = new Set<string>();
  const ordinaryTransport = new Set<string>();
  const addTransport = (transport: Transport) => {
    if (transportSeen.has(transport.key)) return;
    transportSeen.add(transport.key);
    transportQueue.push(transport);
    if (
      transport.tile.tileType === TileType.Wire &&
      !transport.tile.meta.bundle &&
      !transport.tile.meta.partOfChip
    )
      ordinaryTransport.add(transport.tile.id);
  };
  for (const tile of tiles)
    if (
      tile.tileType === TileType.BundleInterface &&
      tile.meta.partOfChip &&
      adapters.has(tile.meta.partOfChip)
    )
      addTransport({ tile, sides: [Side.LEFT], key: `${tile.id}:adapter` });
  for (let index = 0; index < transportQueue.length; index++) {
    const current = transportQueue[index];
    for (const side of current.sides) {
      if (!connections.connectionFlags(current.tile.id)[side]) continue;
      const direction = getGlobalDirection(side, current.tile.meta.rotation)!;
      const neighbor = manager.get(
        getNeighborPosition(Point.fromData(current.tile.location), direction),
      );
      if (
        !neighbor ||
        (current.tile.meta.partOfChip &&
          neighbor.meta.partOfChip === current.tile.meta.partOfChip)
      )
        continue;
      const facing = getLocalSide(opposite(direction), neighbor.meta.rotation)!;
      if (!connections.connectionFlags(neighbor.id)[facing]) continue;
      if (
        neighbor.tileType === TileType.Wire &&
        !neighbor.meta.bundle &&
        !neighbor.meta.partOfChip
      )
        addTransport({ tile: neighbor, sides: Object.values(Side), key: neighbor.id });
      else
        for (const endpoint of passiveByTile.get(neighbor.id) ?? [])
          if (endpoint.side === facing)
            for (const exit of passiveByChannel.get(endpoint.channelKey) ?? [])
              addTransport({
                tile: exit.tile,
                sides: [exit.side],
                key: `${exit.tile.id}:${exit.side}`,
              });
    }
  }
  const visited = new Set<string>();
  for (const first of tiles) {
    if (!first.meta.bundle || visited.has(first.id)) continue;
    const pending = [first],
      group: TileInstance[] = [];
    let attached = false,
      safe = true;
    const bridges = new Set<string>();
    while (pending.length) {
      const tile = pending.pop()!;
      if (visited.has(tile.id)) continue;
      visited.add(tile.id);
      group.push(tile);
      const config = tile.meta.bundle!;
      if (
        tile.tileType !== TileType.Wire ||
        tile.meta.partOfChip ||
        config.width !== first.meta.bundle!.width ||
        Object.values(config.sides ?? {}).some(
          (mapping) =>
            mapping.length !== config.width ||
            mapping.some((lane, index) => lane !== index),
        )
      )
        safe = false;
      for (const side of Object.values(Side)) {
        if (!connections.connectionFlags(tile.id)[side]) continue;
        const direction = getGlobalDirection(side, tile.meta.rotation)!;
        const neighbor = manager.get(
          getNeighborPosition(Point.fromData(tile.location), direction),
        );
        if (!neighbor) continue;
        const facing = getLocalSide(opposite(direction), neighbor.meta.rotation)!;
        if (!connections.connectionFlags(neighbor.id)[facing]) continue;
        if (neighbor.meta.bundle) {
          if (!visited.has(neighbor.id)) pending.push(neighbor);
          continue;
        }
        if (neighbor.meta.cable || ordinaryTransport.has(neighbor.id)) {
          attached = true;
          continue;
        }
        if (neighbor.meta.partOfChip && passiveSides.get(neighbor.id)?.has(facing)) {
          const owner = neighbor.meta.partOfChip;
          const instance = instances.find((item) => item.id === owner);
          if (instance?.bundleWidth && instance.bundleWidth !== config.width)
            safe = false;
          if (!bridges.has(owner)) {
            bridges.add(owner);
            // The whole passive chip must remain safe, including its other
            // independent authored channels; migration never invents a crossing.
            for (const endpoint of passiveOwners.get(owner) ?? []) {
              if (!connections.connectionFlags(endpoint.tile.id)[endpoint.side]) continue;
              const outward = getGlobalDirection(
                endpoint.side,
                endpoint.tile.meta.rotation,
              )!;
              const next = manager.get(
                getNeighborPosition(Point.fromData(endpoint.tile.location), outward),
              );
              if (!next || next.meta.partOfChip === owner) continue;
              if (next.meta.bundle) {
                if (!visited.has(next.id)) pending.push(next);
              } else if (next.meta.cable || ordinaryTransport.has(next.id))
                attached = true;
              else if (
                next.tileType === TileType.BundleInterface &&
                next.meta.partOfChip &&
                adapters.has(next.meta.partOfChip) &&
                getLocalSide(opposite(outward), next.meta.rotation) === Side.LEFT
              )
                attached = true;
              else safe = false;
            }
          }
          continue;
        }
        if (
          neighbor.tileType === TileType.BundleInterface &&
          neighbor.meta.partOfChip &&
          adapters.has(neighbor.meta.partOfChip) &&
          facing === Side.LEFT
        )
          attached = true;
        else safe = false;
      }
    }
    if (!attached) continue;
    if (!safe) {
      for (const tile of group)
        tile.bundleErrors.push(
          'Legacy fixed-lane topology is preserved because it uses remapping, selected-signal taps, or an explicitly lifted chip.',
        );
      continue;
    }
    for (const owner of bridges) {
      const instance = instances.find((item) => item.id === owner);
      if (instance) instance.bundleWidth = undefined;
    }
    for (const tile of group) {
      delete tile.meta.bundle;
      tile.meta.cable = true;
      tile.renderVersion++;
    }
  }
}
